import React from 'react';

export type IconProps = {
  size?: number;
  color?: string;
  className?: string;
};

const strokeIcon =
  (
    children: React.ReactNode,
    viewBox = '0 0 24 24',
    defaultStrokeWidth = 1.8,
  ) =>
  ({
    size = 20,
    color = 'currentColor',
    className,
  }: IconProps) => (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      stroke={color}
      strokeWidth={defaultStrokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {children}
    </svg>
  );

const fillIcon =
  (children: React.ReactNode, viewBox = '0 0 24 24') =>
  ({
    size = 16,
    color = 'currentColor',
    className,
  }: IconProps) => (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill={color}
      className={className}
    >
      {children}
    </svg>
  );

export const EditIcon: React.FC<IconProps> = ({
  size = 18,
  color = 'currentColor',
  className,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    fill="none"
    className={className}
  >
    <path
      d="M1.1454 14.1207C1.18368 13.7761 1.20283 13.6038 1.25496 13.4428C1.30121 13.2999 1.36656 13.164 1.44923 13.0386C1.54241 12.8973 1.66499 12.7747 1.91014 12.5296L12.9154 1.52434C13.8359 0.603865 15.3282 0.603866 16.2487 1.52434C17.1692 2.44482 17.1692 3.9372 16.2487 4.85768L5.24347 15.8629C4.99832 16.1081 4.87574 16.2306 4.73444 16.3238C4.60907 16.4065 4.47311 16.4718 4.33024 16.5181C4.1692 16.5702 3.99691 16.5894 3.65234 16.6276L0.832031 16.941L1.1454 14.1207Z"
      stroke={color}
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const DeleteIcon: React.FC<IconProps> = ({
  size = 17,
  color = 'currentColor',
  className,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 17 17"
    fill="none"
    className={className}
  >
    <path
      d="M5.83203 0.833984H10.832M0.832031 3.33398H15.832M14.1654 3.33398L13.581 12.1001C13.4933 13.4153 13.4494 14.0729 13.1654 14.5715C12.9153 15.0105 12.5381 15.3634 12.0834 15.5837C11.567 15.834 10.9079 15.834 9.58982 15.834H7.07424C5.75612 15.834 5.09706 15.834 4.58065 15.5837C4.126 15.3634 3.74877 15.0105 3.49868 14.5715C3.21462 14.0729 3.17078 13.4153 3.0831 12.1001L2.4987 3.33398"
      stroke={color}
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

export const ViewIcon: React.FC<IconProps> = ({
  size = 15,
  color = '#B3B3B3',
  className,
}) => (
  <svg
    width={size}
    height={(size * 11) / 15}
    viewBox="0 0 15 11"
    fill="none"
    className={className}
  >
    <path
      d="M0.975964 5.9429C0.885173 5.79914 0.839777 5.72726 0.814365 5.61639C0.795277 5.53311 0.795277 5.40178 0.814365 5.3185C0.839777 5.20764 0.885172 5.13576 0.975964 4.992C1.72623 3.80401 3.95948 0.800781 7.36282 0.800781C10.7662 0.800781 12.9994 3.80401 13.7497 4.992C13.8405 5.13576 13.8859 5.20764 13.9113 5.3185C13.9304 5.40178 13.9304 5.53311 13.9113 5.61639C13.8859 5.72726 13.8405 5.79914 13.7497 5.9429C12.9994 7.13089 10.7662 10.1341 7.36282 10.1341C3.95948 10.1341 1.72624 7.13089 0.975964 5.9429Z"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.36282 7.46745C8.46739 7.46745 9.36282 6.57202 9.36282 5.46745C9.36282 4.36288 8.46739 3.46745 7.36282 3.46745C6.25825 3.46745 5.36282 4.36288 5.36282 5.46745C5.36282 6.57202 6.25825 7.46745 7.36282 7.46745Z"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const EyeIcon = strokeIcon(
  <>
    <path d="M2 12c1.2-1.9 4.8-6 10-6s8.8 4.1 10 6c-1.2 1.9-4.8 6-10 6s-8.8-4.1-10-6Z" />
    <circle cx="12" cy="12" r="2.4" />
  </>,
);

export const EyeOffIcon = strokeIcon(
  <>
    <path d="M10.7 5.1A10.6 10.6 0 0 1 12 5c6.5 0 10 7 10 7a18 18 0 0 1-3 4M6.2 6.2A18 18 0 0 0 2 12s3.5 7 10 7a10.5 10.5 0 0 0 4.3-.9" />
    <path d="m3 3 18 18" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </>,
);

export const BackIcon = strokeIcon(
  <path d="M19 12H5M12 19l-7-7 7-7" />,
);

export const ThemeIcon = strokeIcon(
  <>
    <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
    <circle
      cx="17.5"
      cy="10.5"
      r=".5"
      fill="currentColor"
    />
    <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
    <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
    <path d="M12 2a10 10 0 0 0 0 20 2 2 0 0 0 1.5-3.3 2 2 0 0 1 1.5-3.3h1.8A4.2 4.2 0 0 0 21 11a9 9 0 0 0-9-9Z" />
  </>,
);

export const TemplateIcon = strokeIcon(
  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />,
);

export const AdvancedIcon = strokeIcon(
  <>
    <path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h10M18 18h2" />
    <circle cx="16" cy="6" r="2" />
    <circle cx="10" cy="12" r="2" />
    <circle cx="16" cy="18" r="2" />
  </>,
);

export const SaveIcon = strokeIcon(
  <>
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
    <path d="M17 21v-8H7v8M7 3v5h8" />
  </>,
);

export const WorkflowIcon = strokeIcon(
  <>
    <rect x="3" y="3" width="6" height="6" rx="1.5" />
    <rect x="15" y="15" width="6" height="6" rx="1.5" />
    <path d="M6 9v3a3 3 0 0 0 3 3h6" />
  </>,
);

export const AlertIcon = strokeIcon(
  <>
    <path d="M10.3 3.9 2.4 17.6A2 2 0 0 0 4.1 20.6h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9.5v4" />
    <path d="M12 17h.01" />
  </>,
);

export const GripIcon = strokeIcon(
  <>
    <circle cx="9" cy="6" r="1" fill="currentColor" />
    <circle cx="9" cy="12" r="1" fill="currentColor" />
    <circle cx="9" cy="18" r="1" fill="currentColor" />
    <circle cx="15" cy="6" r="1" fill="currentColor" />
    <circle cx="15" cy="12" r="1" fill="currentColor" />
    <circle cx="15" cy="18" r="1" fill="currentColor" />
  </>,
);

export const CursorIcon = strokeIcon(
  <path d="M4 4l6.5 16 2.5-6.5L19.5 11z" />,
  '0 0 24 24',
  1.7,
);

export const CalendarIcon = strokeIcon(
  <>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </>,
);

export const ClockIcon = strokeIcon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </>,
);

export const CalendarClockIcon = strokeIcon(
  <>
    <path d="M21 11V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6" />
    <path d="M3 10h18M8 3v4M16 3v4" />
    <circle cx="17.5" cy="17.5" r="4.5" />
    <path d="M17.5 15.5v2l1.5 1" />
  </>,
);

export const MonthYearIcon = strokeIcon(
  <>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
    <path d="M7 14h4" />
  </>,
);

export const EnvelopeIcon = strokeIcon(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </>,
);

export const PersonIcon = strokeIcon(
  <>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4.4 3.6-7 8-7s8 2.6 8 7" />
  </>,
);

export const PlusIcon = strokeIcon(<path d="M12 5v14M5 12h14" />);

export const MinusIcon = strokeIcon(<path d="M5 12h14" />);

/* Points right; callers rotate it to indicate an expanded state. */
export const ChevronRightIcon = strokeIcon(<path d="m9 6 6 6-6 6" />);

export const ChevronLeftIcon = strokeIcon(<path d="m15 6-6 6 6 6" />);

export const ChevronDownIcon = strokeIcon(<path d="m6 9 6 6 6-6" />);

export const CheckIcon = strokeIcon(<path d="m5 12.5 4.5 4.5L19 7.5" />, undefined, 2.4);

export const ShareIcon = strokeIcon(
  <>
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
  </>,
);

/* A 3x3 dot grid, matching Tabler's IconGridDots — reads as "all the entries"
   rather than the analytics a bar chart implies. */
export const GridDotsIcon = strokeIcon(
  <>
    {[5, 12, 19].map((cy) =>
      [5, 12, 19].map((cx) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.4" fill="currentColor" />
      )),
    )}
  </>,
);

export const CopyIcon = strokeIcon(
  <>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </>,
);

export const CreditCardIcon = strokeIcon(
  <>
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="M2.5 9.5h19" />
    <path d="M6 14.5h3.5" />
  </>,
);

export const IdCardIcon = strokeIcon(
  <>
    <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
    <circle cx="8.7" cy="10.3" r="2.1" />
    <path d="M5.2 15.8a3.6 3.6 0 0 1 7 0" />
    <path d="M14.2 9.3h4.3M14.2 12.6h4.3" />
  </>,
);

export const NumberIcon = strokeIcon(
  <path d="M5 9h14M5 15h14M9 4 7 20M17 4l-2 16" />,
);

export const MapPinIcon = strokeIcon(
  <>
    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21Z" />
    <circle cx="12" cy="9.5" r="2.3" />
  </>,
);

export const GlobeIcon = strokeIcon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.6 3.8 5.7 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.7-3.8-9S9.5 5.6 12 3Z" />
  </>,
);

export const CloseIcon = strokeIcon(
  <>
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </>,
  '0 0 24 24',
  1.8,
);

export const SuccessIcon = fillIcon(
  <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1.1 14.3-4-4 1.4-1.4 2.6 2.6 5.4-5.4 1.4 1.4-6.8 6.8Z" />,
);

export const ErrorIcon = fillIcon(
  <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm3.5 12.1-1.4 1.4L12 13.4l-2.1 2.1-1.4-1.4L10.6 12 8.5 9.9l1.4-1.4L12 10.6l2.1-2.1 1.4 1.4L13.4 12l2.1 2.1Z" />,
);

export const WarningIcon = fillIcon(
  <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 15h-2v-2h2v2Zm0-4h-2V7h2v6Z" />,
);

export const InfoIcon = fillIcon(
  <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 15h-2v-6h2v6Zm0-8h-2V7h2v2Z" />,
);

export const StarFilledIcon = fillIcon(
  <path d="M12 2.8l2.8 5.7 6.3.9-4.6 4.4 1.1 6.2L12 17.1 6.4 20l1.1-6.2-4.6-4.4 6.3-.9L12 2.8Z" />,
);

export const HeartFilledIcon = fillIcon(
  <path d="M12 20.6s-7.9-4.6-9.6-9.8C1.3 7.4 3.4 4 6.9 4c2 0 3.5 1.1 5.1 3 1.6-1.9 3.1-3 5.1-3 3.5 0 5.6 3.4 4.5 6.8-1.7 5.2-9.6 9.8-9.6 9.8Z" />,
);

export const DesktopIcon = strokeIcon(
  <>
    <rect x="3" y="4" width="18" height="12" rx="2" />
    <path d="M8 20h8M12 16v4" />
  </>,
);

export const SparkleIcon = strokeIcon(
  <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3L12 3z" />,
);

export const SingleFormArtwork: React.FC<{ accent: string }> = ({ accent }) => {
  const shadowId = React.useId();
  return (
    <svg width="100%" height="112" viewBox="0 0 220 112" fill="none">
      <defs>
        <filter id={shadowId} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="6" stdDeviation="10" floodColor="#1E1B4B" floodOpacity="0.12" />
        </filter>
      </defs>
      <g filter={`url(#${shadowId})`}>
        <rect x="46" y="14" width="128" height="88" rx="10" fill="#fff" />
      </g>
      <rect x="46" y="14" width="128" height="88" rx="10" stroke="#E5E7EB" />
      <rect x="60" y="28" width="56" height="8" rx="4" fill="#1E1B4B" fillOpacity="0.7" />
      <rect x="60" y="46" width="100" height="7" rx="3.5" fill="#EDEFF3" />
      <rect x="60" y="60" width="100" height="7" rx="3.5" fill="#EDEFF3" />
      <rect x="60" y="74" width="70" height="7" rx="3.5" fill="#EDEFF3" />
      <rect x="60" y="88" width="44" height="10" rx="5" fill={accent} />
    </svg>
  );
};

export const MultiStepFormArtwork: React.FC<{ accent: string }> = ({ accent }) => {
  const shadowId = React.useId();
  return (
    <svg width="100%" height="112" viewBox="0 0 220 112" fill="none">
      <defs>
        <filter id={shadowId} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="6" stdDeviation="10" floodColor="#7C2D12" floodOpacity="0.12" />
        </filter>
      </defs>
      <g filter={`url(#${shadowId})`}>
        <rect x="46" y="14" width="128" height="88" rx="10" fill="#fff" />
      </g>
      <rect x="46" y="14" width="128" height="88" rx="10" stroke="#E5E7EB" />
      <circle cx="66" cy="32" r="7" fill={accent} />
      <path d="M63 32l2 2 4-4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="66" y="45" width="2" height="14" fill="#E5E7EB" />
      <circle cx="66" cy="66" r="7" fill="#fff" stroke={accent} strokeWidth="1.6" />
      <circle cx="66" cy="66" r="2.5" fill={accent} />
      <rect x="66" y="79" width="2" height="14" fill="#E5E7EB" />
      <circle cx="66" cy="100" r="7" fill="#F3F4F6" stroke="#E5E7EB" strokeWidth="1.6" />
      <rect x="82" y="27" width="76" height="9" rx="4.5" fill="#1E1B4B" fillOpacity="0.7" />
      <rect x="82" y="61" width="76" height="7" rx="3.5" fill="#EDEFF3" />
      <rect x="82" y="72" width="56" height="7" rx="3.5" fill="#EDEFF3" />
      <rect x="82" y="94" width="76" height="7" rx="3.5" fill="#F3F4F6" />
    </svg>
  );
};


export const TemplatePreviewArtwork: React.FC<{ fieldCount: number; stepCount: number; accent: string }> = ({
  fieldCount,
  stepCount,
  accent,
}) => {
  const shadowId = React.useId();
  const rows = Math.min(Math.max(fieldCount, 2), 4);
  return (
    <svg width="100%" height="88" viewBox="0 0 220 88" fill="none">
      <defs>
        <filter id={shadowId} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="5" stdDeviation="8" floodColor="#1E1B4B" floodOpacity="0.1" />
        </filter>
      </defs>
      <g filter={`url(#${shadowId})`}>
        <rect x="40" y="10" width="140" height="68" rx="10" fill="#fff" />
      </g>
      <rect x="40" y="10" width="140" height="68" rx="10" stroke="#E5E7EB" />
      <rect x="54" y="22" width="52" height="7" rx="3.5" fill="#1E1B4B" fillOpacity="0.7" />
      {Array.from({ length: rows }).map((_, i) => (
        <rect
          key={i}
          x="54"
          y={36 + i * 11}
          width={i % 2 === 0 ? 112 : 84}
          height="6"
          rx="3"
          fill="#EDEFF3"
        />
      ))}
      {stepCount > 1 && (
        <>
          <circle cx="54" cy="72" r="3" fill={accent} />
          <circle cx="64" cy="72" r="3" fill="#D9D9F5" />
          <circle cx="74" cy="72" r="3" fill="#D9D9F5" />
        </>
      )}
    </svg>
  );
};


